import * as Sentry from "@sentry/react";
import React from "react";
import type { LoaderFunctionArgs } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import ActionItems from "./components/ActionItems";
import Workspaces from "./components/Workspaces";

import type { SearchServiceSort } from "../../../api/hooks";

import { currentUserQuery, meetingWorkspacesSearchQuery } from "../../../api/queries";
import { type SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";
import { queryClient } from "../../../api/queryClient";
import type { MeResponse } from "../../../api/services";

import { convertWorkspaceFiltersToSearchParams } from "./utils";

import { workspacesTableConfig } from "./config";

// Data loader function
export const meetingWorkspacesLoader = ({ params }: LoaderFunctionArgs) => {
	const queries: (() => SimplifiedQuery)[] = [];

	queries.push(() => {
		/** Search service query */
		// acting user ID is required for meeting workspaces search
		const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;
		const userId = currentUser?.me?.authorization?.payload?.user_id;

		// Get query state for the page from URL (filters, sort, search, page) using the same logic as useInfoPageTemplateQueryState
		const pageNumber = Number(params?.pageNumber) || workspacesTableConfig.defaultPageNumber;
		const sort = {
			name: params?.name || workspacesTableConfig.defaultSortName,
			direction: params?.direction || workspacesTableConfig.defaultSortDirection,
		};
		const searchValue = params?.search || "";
		const filters = JSON.parse(params?.filters || "{}");
		if (Object.keys(filters).length > 0) {
			//convert all date strings to date objects
			// we can find dates by looking for the string "date-" at the beginning of the value
			for (const key in filters) {
				if (typeof filters[key] === "string" && filters[key].startsWith("date-")) {
					filters[key] = new Date(filters[key].replace("date-", "")) as Date;
				}
			}
		}

		// Make sure status is always included (in case we recieve a bad URL)
		if (filters.status === undefined) {
			filters.status = "active";
		}

		return meetingWorkspacesSearchQuery(
			convertWorkspaceFiltersToSearchParams(
				filters,
				searchValue,
				sort as SearchServiceSort,
				pageNumber,
				userId || ""
			)
		);
	});

	return dataLoader([[currentUserQuery], queries]);
};

const MeetingWorkspaces = () => {
	const [searchParams] = useSearchParams();
	const tab = searchParams.get("tab");

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			{tab === "action-items" ? <ActionItems /> : <Workspaces />}
		</Sentry.ErrorBoundary>
	);
};

export default MeetingWorkspaces;
